import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';

import { ProxyService } from '../proxy.service';
import { CreateProxyRequest, ProxyModel, UpdateProxyRequest } from '../proxy.type';

interface ProxyDialogData {
  mode: 'create' | 'edit';
  proxy?: ProxyModel;
}

interface ProxyForm {
  address: FormControl<string>;
  port: FormControl<number>;
  username: FormControl<string>;
  password: FormControl<string>;
  active: FormControl<boolean>;
  bulkText: FormControl<string>;
}

@Component({
  selector: 'app-proxy-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTabsModule,
  ],
  templateUrl: './proxy-form-dialog.html',
  styleUrl: './proxy-form-dialog.scss',
})
export class ProxyFormDialog implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject(MatDialogRef<ProxyFormDialog>);
  private proxyService = inject(ProxyService);
  private snackBar = inject(MatSnackBar);
  readonly data = inject<ProxyDialogData>(MAT_DIALOG_DATA);

  loading = false;
  selectedCreateTabIndex = 0;

  proxyForm: FormGroup<ProxyForm> = this.fb.nonNullable.group({
    address: ['', Validators.required],
    port: [50100, [Validators.required, Validators.pattern('^[0-9]+$')]],
    username: ['', Validators.required],
    password: ['', Validators.required],
    active: [true],
    bulkText: [''],
  });

  get isEdit(): boolean {
    return this.data.mode === 'edit';
  }

  ngOnInit(): void {
    if (this.isEdit && this.data.proxy) {
      const p = this.data.proxy;

      this.proxyForm.patchValue({
        address: p.address,
        port: p.port,
        username: p.username,
        password: p.password,
        active: p.active,
      });
    }
  }

  submit(): void {
    if (this.isEdit) {
      this.submitSingle();
      return;
    }

    const bulkText = this.proxyForm.controls.bulkText.value.trim();

    if (this.selectedCreateTabIndex === 1) {
      this.submitBulk(bulkText);
      return;
    }

    this.submitSingle();
  }

  private submitSingle(): void {
    if (
      this.proxyForm.controls.address.invalid ||
      this.proxyForm.controls.port.invalid ||
      this.proxyForm.controls.username.invalid ||
      this.proxyForm.controls.password.invalid
    ) {
      this.proxyForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.proxyForm.disable();

    const v = this.proxyForm.getRawValue();

    const request: CreateProxyRequest = {
      address: v.address,
      port: Number(v.port),
      username: v.username,
      password: v.password,
      active: v.active,
    };

    const op$ = this.isEdit
      ? this.proxyService.updateProxy(this.data.proxy!.id, this.buildChangedRequest(request))
      : this.proxyService.createProxy(request);

    op$.subscribe({
      next: () => {
        this.snackBar.open(
          this.isEdit ? 'Proxy updated successfully' : 'Proxy created successfully',
          'Close',
          { duration: 3000 }
        );

        this.dialogRef.close(true);
      },
      error: () => {
        this.snackBar.open(
          this.isEdit ? 'Error updating proxy' : 'Error creating proxy',
          'Close',
          { duration: 3000 }
        );

        this.loading = false;
        this.proxyForm.enable();
      },
    });
  }

  private submitBulk(text: string): void {
    let proxies: CreateProxyRequest[];

    try {
      proxies = this.parseProxyList(text);
    } catch (error) {
      this.snackBar.open(
        error instanceof Error ? error.message : 'Invalid proxy list',
        'Close',
        { duration: 5000 }
      );
      return;
    }

    if (!proxies.length) {
      this.snackBar.open('No proxies found', 'Close', { duration: 3000 });
      return;
    }

    this.loading = true;
    this.proxyForm.disable();

    this.proxyService.createProxies(proxies).subscribe({
      next: () => {
        this.snackBar.open(`${proxies.length} proxies created successfully`, 'Close', {
          duration: 3000,
        });

        this.dialogRef.close(true);
      },
      error: () => {
        this.snackBar.open('Error creating one or more proxies', 'Close', {
          duration: 3000,
        });

        this.loading = false;
        this.proxyForm.enable();
      },
    });
  }

  private buildChangedRequest(request: CreateProxyRequest): UpdateProxyRequest {
    const proxy = this.data.proxy;

    if (!proxy) {
      return request;
    }

    const changed: UpdateProxyRequest = {};

    if (request.address !== proxy.address) {
      changed.address = request.address;
    }

    if (request.port !== proxy.port) {
      changed.port = request.port;
    }

    if (request.username !== proxy.username) {
      changed.username = request.username;
    }

    if (request.password !== proxy.password) {
      changed.password = request.password;
    }

    if (request.active !== proxy.active) {
      changed.active = request.active;
    }

    return changed;
  }

  private parseProxyList(text: string): CreateProxyRequest[] {
    return text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        const match = line.match(/^([^:]+):([^@]+)@([^:]+):(\d+)$/);

        if (!match) {
          throw new Error(`Invalid proxy format: ${line}`);
        }

        const [, username, password, address, port] = match;

        return {
          address,
          port: Number(port),
          username,
          password,
          active: true,
        };
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
